import { Drawer } from 'antd'
import pluralize from 'pluralize'
import Select from '@atlaskit/select'
import ReactSelect from 'react-select'
import { useSnackbar } from 'notistack'
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { normalize, useListData, usePet, useToggle, patientName, notifOps} from '../../modules'
import PreauthCheck from '../modals/preauthCheck'

// const { Option } = Select

const BillPatient = ({ visible, toggle, patient, bill, category, categories, onClose }) => {
    const [cat, setCat] = useState({ label: category, value: category })
    const [selected, setSelected] = useState([])
    const [string, setString] = useState()
    const [pString, setPstring] = useState()
    const [priceList, setList] = useState()
    const [payment, setPayment] = useState(false)
    const [lists, setLists] = useState([])
    const [preauthIndex, setPreauthIndex] = useState(null)
    const [standalone, setStandalone] = useState(false)
    const [pendingRetry, setPendingRetry] = useState(false)
    const [retriedAsStandalone, setRetriedAsStandalone] = useState(false)

    const { enqueueSnackbar } = useSnackbar()
    const history = useHistory()

    const [select, 
        // toggleSelect
    ] = useToggle()
    const [inPatient, toggleInPatient] = useToggle()
    const [preauthVisible, , setPreauthVisible] = useToggle()

    const close = () => {
        toggle()
        setTimeout(onClose, 300)
    }

    const [
        { loading: iLoading, fetch: fetchItems },
        { list: items },
        { page = 1, limit = 10 }
    ] = useListData('saleItems', { string, routingGroup: cat.label }, { notify: true }, 'items')

    const [
        { loading: schemesLoading },
        { list: patientSchemes = [] },
    ] = useListData('schemesByPatient', { id: patient?._id, string: '' }, { notify: false }, 'schemes', 100)

    const [{ loading }, getPriceLists] = usePet('priceLists', { limit: 100, order: 1 }, { notify: true, callback: async ({ data }) => {
        let lsts = data?.lists?.map(lst => ({ label: lst?.name, value: lst?._id, ...lst }))
        setLists(lsts) 
    } })

    useEffect(() => {
        if (!lists?.length) return

        const currentStillValid = lists.find(list => String(list?._id ?? list?.value) === String(priceList?._id ?? priceList?.value))
        if (currentStillValid) return

        const billSchemeId = bill?.scheme?._id ?? bill?.scheme
        const matchingPatientScheme = patientSchemes.find(item => String(item?._id) === String(billSchemeId))
        const preferredPatientScheme = matchingPatientScheme ?? patientSchemes.find(item => item?.main) ?? patientSchemes[0]
        const preferredInsurerId = preferredPatientScheme?.scheme?.operatedBy?._id ?? preferredPatientScheme?.scheme?.operatedBy

        const insurerMatchedList = preferredInsurerId
            ? lists.find(list => String(list?.insurance?._id ?? list?.insurance) === String(preferredInsurerId))
            : null

        const cashList = lists.find(list => String(list?.name ?? '').trim().toLowerCase() === 'cash')
        const fallbackList = insurerMatchedList ?? cashList ?? lists[0]

        setList(fallbackList)
    }, [lists, patientSchemes, bill?._id]) // eslint-disable-line react-hooks/exhaustive-deps

    const onComplete = (_, notif, ops) => {
        notif('Items have been added to bill', ops('success'))
        setStandalone(false)
        setRetriedAsStandalone(false)

        if (payment)
            history.push('/app/cashier/bills')

        close()
    }

    const [{ loading: bLoading, error: bError }, billPatient] = usePet('billPatient', {
        patient: patient?._id,
        items: selected.map(({ _id, quantity, price, preauth }) => ({
            item: _id,
            quantity,
            price,
            preauth: preauth?._id ?? preauth
        })),
        list: priceList?.value,
        station: localStorage.getItem('station') ?? 'Cashier',
        inPatient,
        standalone
    }, { notify: false, callback: onComplete, manual: true, load: { msg: 'Adding items to bill...' } })

    // billPatient's payload closes over the render it was declared in, so a state
    // update made in the same handler that calls billPatient() would still use the
    // stale `standalone` value - wait for the next render (pendingRetry pattern).
    useEffect(() => {
        if (!pendingRetry) return

        setPendingRetry(false)
        billPatient()
    }, [pendingRetry, standalone]) // eslint-disable-line react-hooks/exhaustive-deps

    // If billing fails because an open bill on this patient is on a different
    // scheme than what was just selected, silently retry as a standalone (new,
    // separate) order instead of surfacing a raw error - the cashier shouldn't
    // need to understand "different scheme" to know what to do next.
    useEffect(() => {
        if (!bError) return

        const message = bError?.response?.data?.message ?? bError?.message ?? ''

        if (message.includes('different scheme') && !retriedAsStandalone) {
            enqueueSnackbar('This patient already has an open bill under a different scheme. We are creating a separate standalone bill for these items.', notifOps('warning'))
            setRetriedAsStandalone(true)
            setStandalone(true)
            setPendingRetry(true)
            return
        }

        enqueueSnackbar(message || 'An error occurred while billing the patient', notifOps('warning'))
    }, [bError]) // eslint-disable-line react-hooks/exhaustive-deps

    const search = str => {
        if (str?.trim()?.length >= 1 || !str) {
            setString(str)
            fetchItems()
        }  
    }

    const onCategoryChange = ct => {
        setCat(ct)
        fetchItems()
    }

    const changePriceList = val => {
        setList(val)
        setSelected([])
    }

    const modifyQty = (i, val) => {
        let temp = [...selected]
        temp[i].quantity += val

        if (temp[i].quantity <= 0)
            temp = temp.filter((_, k) => k !== i)

        setSelected(temp)
    }

    const clear = () => setSelected([])

    const attachPreauth = preauth => {
        let temp = [...selected]
        temp[preauthIndex].preauth = preauth
        setSelected(temp)
        setPreauthVisible(false)
        setPreauthIndex(null)

        setTimeout(() => {
            const nextIndex = temp.findIndex(item => item?.preauthRequired && item?.preauthCategory && !item?.preauth)
            if (nextIndex < 0) billPatient()
            else {
                setPreauthIndex(nextIndex)
                setPreauthVisible(true)
            }
        }, 100)
    }

    const submitBill = (receivePayment = false) => {
        setPayment(receivePayment)
        const nextIndex = selected.findIndex(item => item?.preauthRequired && item?.preauthCategory && !item?.preauth)
        if (nextIndex < 0) return billPatient()

        setPreauthIndex(nextIndex)
        setPreauthVisible(true)
    }

    const setItems = vals => {
        if (vals?.length > 0 && !vals.some(itm => itm.prices.find(price => price?.priceList === priceList?._id))) {
            enqueueSnackbar(`One or more of the selected items is not listed in the ${priceList?.label} price list`, notifOps('warning'))
            return
        }

        vals = vals.map(itm => {
            let entry = itm.prices.find(price => price?.priceList === priceList?._id)
            
            itm.priceList = entry?.priceList
            itm.price = entry?.price
            return itm
        })

        setSelected(vals)
    }

    return (
        <Drawer
            closable
            maskClosable
            visible={visible}
            onClose={close}
            footer={
                <div className="w-full text-right">
                    <button onClick={ () => submitBill(true) } className="text-white hisani-btn-orange mr-3 font-medium rounded-lg text-sm px-5 py-2.5 text-center">Bill & Receive Payment</button>
                    <button disabled={ bLoading } onClick={ () => submitBill(false) } className="text-white hisani-btn-green mr-3 font-medium rounded-lg text-sm px-5 py-2.5 text-center">Bill Patient</button>
                    <button onClick={ clear } className="text-white bg-gray-500 hover:bg-gray-600 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center">Clear Items</button>
                </div>
            }
            width={800}>
            <p className="font-extrabold text-3xl mb-0">Bill Patient</p>
            {
                preauthVisible &&
                <PreauthCheck
                    visible={ preauthVisible }
                    toggle={ () => {
                        setPreauthVisible(false)
                        setPreauthIndex(null)
                    } }
                    patient={ patient }
                    saleItem={ preauthIndex >= 0 ? selected[preauthIndex] : null }
                    onCreated={ () => {
                        setPreauthVisible(false)
                        setPreauthIndex(null)
                    } }
                    onProceed={ attachPreauth } />
            }
            <div className="min-w-full my-6 md:mb-0">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                    Bill To
                </label>
                <div className="flex items-center justify-between min-w-full">
                    <Select
                        inputId="single-select-example"
                        className="single-select w-60"
                        classNamePrefix="react-select"
                        onChange={ onCategoryChange }
                        value={ cat }
                        options={ categories.map(cat => ({ label: cat, value: cat })) }
                        placeholder="Choose Category" />                
                    <ReactSelect
                        className="w-44"
                        onChange={ changePriceList }
                        value={ priceList }
                        isLoading={ loading }
                        options={ lists }
                        placeholder="Select Price List" />
                </div>
                {
                    priceList &&
                    <p className="text-xs text-gray-500 mt-2 mb-0">
                        Billing will use the {priceList?.label} price list{bill?.scheme ? ' based on the existing bill scheme context' : patientSchemes?.length > 0 ? ' based on the patient scheme context' : ''}.
                    </p>
                }
            </div>
            <div className="w-full my-6 md:mb-0">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                    Select Items
                </label>
                <ReactSelect
                    isMulti
                    onInputChange={search}
                    onChange={ setItems }
                    value={ selected }
                    isLoading={ iLoading }
                    styles={{
                        option: (styles, { data }) => { 
                            let { current } = data
                            return {
                                ...styles, 
                                backgroundColor: current <= 0 ? undefined : styles.backgroundColor,
                                color: current <= 0 ? 'gray' : styles.color,
                                cursor: current <= 0 ? 'not-allowed' : 'default'
                            }
                        }
                    }}
                    options={ items?.map(itm => ({ label: `${itm.name}${itm.current <= 0 ? ` - Ksh. ${itm.price} (Out of Stock)` : '' }`, value: itm._id, ...itm, quantity: 1, isDisabled: itm?.current <= 0 })) }
                    placeholder={ `What ${bill ? 'else' : '' } is ${patientName(patient?.name) } paying for?` } />
            </div>
            <div className="p-3 mt-4">
                {
                    selected.length > 0 ?
                        <div className="overflow-x-auto">
                            <table className="table-auto w-full">
                                {/* Table header */}
                                <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                    <tr>
                                        <th className="p-2">
                                            <div className="font-semibold text-center">{select ? 'Selected' : 'No'}</div>
                                        </th>
                                        <th className="p-2">
                                            <div className="font-semibold text-left">Item</div>
                                        </th>
                                        {
                                            selected.some(item => item.ref) &&
                                            <th className="p-2">
                                                <div className="font-semibold text-center">Stock Level</div>
                                            </th>
                                        }
                                        <th className="p-2">
                                            <div className="font-semibold text-center">Quantity</div>
                                        </th>
                                        <th className="p-2">
                                            <div className="font-semibold text-center">Total</div>
                                        </th>
                                    </tr>
                                </thead>
                                {/* Table body */}
                                <tbody className="text-sm font-medium divide-y divide-gray-100">
                                    {
                                        selected.map((item, key) =>
                                            <tr>
                                                <td className="p-2">
                                                    <div className="text-center">{((page - 1) * limit) + (key + 1)}.</div>
                                                </td>
                                                <td className="p-2">
                                                    <div className="">{ item.name }</div>
                                                    <p className="text-gray-500 text-xs italic">Unit Price: Ksh. { item.price }</p>
                                                </td>
                                                {
                                                    selected.some(item => item.ref) &&
                                                    <th className="p-2">
                                                        <div className={ `font-semibold text-center ${ item?.quantity > item?.current ? 'text-red-500' : 'text-green-500' }` }>{ item?.ref && Number.isFinite(item?.current) ? `${item?.current} ${pluralize(item?.ref?.issuedIn ?? 'item', item?.current)}` : 'N/A' }</div>
                                                    </th>
                                                }
                                                <td className="p-2 flex items-center justify-center">
                                                    <button onClick={() => modifyQty(key, -1)} type="button" className="px-4 py-3 rounded-md outline-none focus:ring-4 shadow-lg">-</button>
                                                    <span className="text-center px-3">{ item.quantity }</span>
                                                    <button onClick={() => modifyQty(key, 1)} type="button" className="px-4 py-3 rounded-md outline-none focus:ring-4 shadow-lg">+</button>
                                                </td>
                                                <td className="p-2">
                                                    <div className="text-center">Ksh. { item.price * item.quantity }</div>
                                                </td>
                                            </tr>
                                        )
                                    }
                                </tbody>
                            </table>
                            <div className="flex flex-col items-end mb-3 p-3">
                                <p className="font-semibold text-lg underline mb-2">Total Selected</p>
                                <p>Total: Ksh. { normalize(selected.reduce((total, itm) => total + (itm.price * itm.quantity), 0)) }</p>
                            </div>
                        </div>
                        :
                        <div className="w-full h-16 mt-4 flex items-center justify-center">
                            <p className="font-semibold text-base">You can add items by selecting from the list above</p>
                        </div>
                }
            </div>
        </Drawer>
    )
}

export default BillPatient
